import React, { FC, Fragment, useEffect, useState } from 'react';
import i18n from '../../../i18n';
import { makeStyles } from '@material-ui/core/styles';
import { WrappedFieldProps } from 'redux-form';

import { range } from 'core/utils/utils';

import { Icon } from '../Icon/Icon';
import { LargeButton, LargeButtonColor } from '../LargeButton/LargeButton';
import { Stage } from '../Stage/Stage';
import { StageItem } from '../Stage/StageItem';
import { SwitchControl } from '../SwitchControl/SwitchControl';
import { T } from '../T/T';
import { TranslatableEnum } from 'components/T/GetTranslationForEnum';
import { Text, TextTypography } from '../Text/Text';
import { TextButton } from '../TextButton/TextButton';
import { TimePickerControl } from '../TimePickerControl/TimePickerControl';

import { ReactComponent as EditIcon } from 'assets/icons/main-16/pencil-o--16.svg';
import { DateTime } from 'luxon';
import { DialogNew } from '../Dialog/DialogNew';
import { useSelector } from 'react-redux';
import { isUIEnabledSelector } from '../../state/app/selectors';

export interface WorkingHoursDialogProps {
    label: string;
    value: any;
    onChange: (value: any) => void;
    // Optional:
    error?: string;
}

const useStyles = makeStyles(theme => ({
    content: {},
    description: {
        display: 'block',
    },
    container: {
        marginTop: theme.spacing(3),
    },
    field: {
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        marginBottom: theme.spacing(2),
        alignItems: 'center',
        justifyContent: 'space-between',
        height: 48,
        '&:last-child': {
            marginLeft: -8,
            marginBottom: 0,
        },
    },
    fieldDay: {
        display: 'flex',
        flexDirection: 'row',
        marginBottom: theme.spacing(2),
        alignItems: 'center',
        height: 48,
        [theme.breakpoints.down(375)]: {
            height: '100%',
            display: 'block',
            flexDirection: 'columns',
        },
    },
    fieldLeft: {
        display: 'flex',
        whiteSpace: 'nowrap',
        // flex: 1,
    },
    dash: {
        lineHeight: '42px',
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
    },
    fieldRight: {
        maxWidth: 178,
        display: 'contents',
        height: '100%',
        justifyContent: 'space-between',
        [theme.breakpoints.up(375)]: {
            maxWidth: 146,
        },
    },
    fieldRightDays: {
        maxWidth: 178,
        display: 'contents',
        height: '100%',
        justifyContent: 'space-between',
        [theme.breakpoints.down(375)]: {
            maxWidth: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 16,
            marginBottom: 8,
            '& button': {
                padding: 0,
                '& span': {
                    display: 'flex',
                },
            },
        },
    },
    timepicker: {
        '& input': {
            minHeight: 48,
            padding: 0,
            textAlign: 'center',
            [theme.breakpoints.down(375)]: {
                minHeight: 37,
            },
        },
    },
    copyForText: {
        lineHeight: 2,
    },
    actions: {
        display: 'flex',
        flexGrow: 0,
        flexBasis: 'auto',
        paddingTop: theme.spacing(1.5),
        paddingBottom: theme.spacing(2.5),
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
    },
    action: {
        flex: 1,
    },
    actionSpace: {
        flexBasis: theme.spacing(2),
    },
}));

export const initialWorkingHoursValue: Record<number, any> = range(0, 6).reduce(
    (acc, k) => ({
        ...acc,
        [k]: {
            workingHoursEnabled: true,
            workingHoursFrom: DateTime.fromObject({ hour: 9, minute: 0 }),
            workingHoursTo: DateTime.fromObject({ hour: 19, minute: 0 }),
            breakEnabled: true,
            breakFrom: DateTime.fromObject({ hour: 13, minute: 0 }),
            breakTo: DateTime.fromObject({ hour: 14, minute: 0 }),
            roundTheClockEnabled: false,
        },
    }),
    {},
);

export const WorkingHoursControl: FC<WorkingHoursDialogProps> = (props: WorkingHoursDialogProps) => {
    const { t } = i18n.useTranslation();
    const classes = useStyles();
    const isUIEnabled = useSelector(isUIEnabledSelector);
    const [model, updateModel] = useState(props.value);
    useEffect(() => updateModel(props.value), [props.value]);
    const [activeTab, setActiveTab] = useState(0);
    const [showDialog, setShowDialog] = useState(false);
    const tabModel = model[activeTab];

    return (
        <Fragment>
            <LargeButton color={LargeButtonColor.TRANSPARENT} onClick={() => setShowDialog(true)}>
                <Icon component={EditIcon} size={16} svgSize={16} />
                &nbsp;
                {props.label}
            </LargeButton>
            {showDialog && (
                <DialogNew
                    title={t('onboarding:workingHours.title')}
                    onClose={() => setShowDialog(false)}
                    onSubmit={() => {
                        props.onChange(model);
                        setShowDialog(false);
                    }}
                    leftButton={t('common:settings.closeButton')}
                    rightButton={t('onboarding:settings:saveButton')}
                    isUIEnabled={isUIEnabled}
                    width={423}
                >
                    <div className={classes.content}>
                        <Text className={classes.description} typography={TextTypography.PARA}>
                            {t('onboarding:workingHours.description')}
                        </Text>
                        <Stage value={activeTab} onChange={setActiveTab}>
                            {range(0, 6).map(i => (
                                <StageItem
                                    key={i}
                                    label={
                                        <Text component="span" typography={TextTypography.BODY_HEADING}>
                                            <T enum={TranslatableEnum.DAY_OF_WEEK_BY_NUMBER}>{i}</T>
                                        </Text>
                                    }
                                />
                            ))}
                        </Stage>
                        <div className={classes.container}>
                            <div>
                                <div className={classes.field}>
                                    <div className={classes.fieldLeft}>
                                        <SwitchControl
                                            disabled={tabModel?.roundTheClockEnabled}
                                            label={t('onboarding:workingHours.workDay')}
                                            value={tabModel?.workingHoursEnabled}
                                            onChange={value =>
                                                updateModel({
                                                    ...model,
                                                    [activeTab]: { ...tabModel, workingHoursEnabled: value },
                                                })
                                            }
                                        />
                                    </div>
                                    <div className={classes.fieldRight}>
                                        <div className={classes.timepicker}>
                                            <TimePickerControl
                                                isDisabled={
                                                    !tabModel?.workingHoursEnabled || tabModel?.roundTheClockEnabled
                                                }
                                                value={tabModel?.workingHoursFrom}
                                                onChange={(value: any) =>
                                                    updateModel({
                                                        ...model,
                                                        [activeTab]: { ...tabModel, workingHoursFrom: value },
                                                    })
                                                }
                                            />
                                        </div>
                                        <span className={classes.dash}>&ndash;</span>
                                        <div className={classes.timepicker}>
                                            <TimePickerControl
                                                isDisabled={
                                                    !tabModel?.workingHoursEnabled || tabModel?.roundTheClockEnabled
                                                }
                                                value={tabModel?.workingHoursTo}
                                                onChange={(value: any) =>
                                                    updateModel({
                                                        ...model,
                                                        [activeTab]: { ...tabModel, workingHoursTo: value },
                                                    })
                                                }
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className={classes.field}>
                                    <div className={classes.fieldLeft}>
                                        <SwitchControl
                                            disabled={tabModel?.roundTheClockEnabled || !tabModel?.workingHoursEnabled}
                                            label={t('onboarding:workingHours.break')}
                                            value={tabModel?.breakEnabled}
                                            onChange={value =>
                                                updateModel({
                                                    ...model,
                                                    [activeTab]: { ...tabModel, breakEnabled: value },
                                                })
                                            }
                                        />
                                    </div>
                                    <div className={classes.fieldRight}>
                                        <div className={classes.timepicker}>
                                            <TimePickerControl
                                                isDisabled={
                                                    !tabModel?.breakEnabled ||
                                                    !tabModel?.workingHoursEnabled ||
                                                    tabModel?.roundTheClockEnabled
                                                }
                                                value={tabModel?.breakFrom}
                                                onChange={(value: any) =>
                                                    updateModel({
                                                        ...model,
                                                        [activeTab]: { ...tabModel, breakFrom: value },
                                                    })
                                                }
                                            />
                                        </div>
                                        <span className={classes.dash}>&ndash;</span>
                                        <div className={classes.timepicker}>
                                            <TimePickerControl
                                                isDisabled={
                                                    !tabModel?.breakEnabled ||
                                                    !tabModel?.workingHoursEnabled ||
                                                    tabModel?.roundTheClockEnabled
                                                }
                                                value={tabModel?.breakTo}
                                                onChange={(value: any) =>
                                                    updateModel({
                                                        ...model,
                                                        [activeTab]: { ...tabModel, breakTo: value },
                                                    })
                                                }
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className={classes.field}>
                                    <div className={classes.fieldLeft}>
                                        <SwitchControl
                                            label={t('onboarding:workingHours.roundTheClock')}
                                            value={tabModel?.roundTheClockEnabled}
                                            onChange={value =>
                                                updateModel({
                                                    ...model,
                                                    [activeTab]: { ...tabModel, roundTheClockEnabled: value },
                                                })
                                            }
                                        />
                                    </div>
                                </div>
                                {tabModel && (
                                    <div className={classes.fieldDay}>
                                        <div className={classes.fieldLeft}>
                                            <Text className={classes.copyForText} typography={TextTypography.PARA}>
                                                {t('onboarding:workingHours.copyFor')}
                                            </Text>
                                        </div>
                                        <div className={classes.fieldRightDays}>
                                            <TextButton
                                                onClick={() => {
                                                    updateModel({
                                                        ...model,
                                                        ...range(0, 4).reduce(
                                                            (acc, k) => ({ ...acc, [k]: tabModel }),
                                                            {},
                                                        ),
                                                    });
                                                }}
                                            >
                                                {t('onboarding:workingHours.workdays')}
                                            </TextButton>
                                            <TextButton
                                                onClick={() => {
                                                    updateModel({
                                                        ...model,
                                                        ...range(5, 6).reduce(
                                                            (acc, k) => ({ ...acc, [k]: tabModel }),
                                                            {},
                                                        ),
                                                    });
                                                }}
                                            >
                                                {t('onboarding:workingHours.weekends')}
                                            </TextButton>
                                            <TextButton
                                                onClick={() => {
                                                    updateModel({
                                                        ...model,
                                                        ...range(0, 6).reduce(
                                                            (acc, k) => ({ ...acc, [k]: tabModel }),
                                                            {},
                                                        ),
                                                    });
                                                }}
                                            >
                                                {t('onboarding:workingHours.all')}
                                            </TextButton>
                                        </div>
                                    </div>
                                )}

                                <div className={classes.field}>
                                    <TextButton
                                        onClick={() => {
                                            updateModel({
                                                ...initialWorkingHoursValue,
                                            });
                                        }}
                                    >
                                        {t('onboarding:workingHours.setDefault')}
                                    </TextButton>
                                </div>
                            </div>
                        </div>
                    </div>
                </DialogNew>
            )}
        </Fragment>
    );
};

export interface WorkingHoursControlFieldOwnProps {
    label: string;
}

export type WorkingHoursControlFieldProps = WorkingHoursControlFieldOwnProps & WrappedFieldProps;

export const WorkingHoursControlField = (props: WorkingHoursControlFieldProps) => {
    const {
        input,
        label,
        meta: { touched, invalid, error },
    } = props;
    return <WorkingHoursControl label={label} error={touched && invalid ? error : undefined} {...input} />;
};
